import { dev } from '$app/environment';
import { ClientSettings } from '$lib/ClientSettings';
import { withBaseUrl } from '$lib/svelte-utils/utils/urlUtils';
import frontmatter from 'front-matter';

export type PostMetaData = DocsMetaData & {
	author: string;
	coverImageUrl: string;
	coverImageHint?: string;
	overlayImage?: boolean;
};

export type DocsMetaData = {
	title: string;
	summary: string;
	published: Date;
	updated?: string;
	private?: boolean;
	slug: string;
	shortTitle: string;
};

export async function fetchBlogPosts(targetSlug: string | null) {
	return fetchArticles(targetSlug, 'blog');
}

export async function fetchDocArticles(targetSlug: string | null) {
	const articles = await fetchArticles(targetSlug, 'docs');
	// TODO: Load dynamically?
	articles.push({
		published: new Date(2024, 9, 1),
		slug: 'commands',
		title: 'Commands in Codelantis',
		shortTitle: 'Commands',
		summary:
			'Commands in Codelantis are a superpower for code reviews. This articles shows the available commands in Codelantis.'
	});
	return articles;
}

async function fetchArticles(targetSlug: string | null, type: 'blog'): Promise<PostMetaData[]>;
async function fetchArticles(targetSlug: string | null, type: 'docs'): Promise<DocsMetaData[]>;
async function fetchArticles(targetSlug: string | null, type: 'blog' | 'docs') {
	let articleMetaInfos: Record<string, string>;
	if (type == 'blog') {
		articleMetaInfos = import.meta.glob('/blogposts/**/*.meta.md', {
			as: 'raw',
			eager: true
		});
	} else {
		articleMetaInfos = import.meta.glob('/docs/**/*.meta.md', {
			as: 'raw',
			eager: true
		});
	}

	const now = new Date();
	return Object.keys(articleMetaInfos)
		.filter((path) => targetSlug == null || path.endsWith(targetSlug + '.meta.md'))
		.map((path) => {
			const content = articleMetaInfos[path];
			return getFrontMatter(content);
		})
		.filter((meta1) => meta1.published < now && (!meta1.private || dev))
		.sort((meta1, meta2) => (meta1.published < meta2.published ? 1 : -1));
}

export function getFrontMatter(content: string) {
	const parsed = frontmatter(content);
	return parsed.attributes as PostMetaData;
}

export async function getArticleForSlug(
	slug: string,
	type: 'docs'
): Promise<{ content: string; metaData: DocsMetaData } | null>;
export async function getArticleForSlug(
	slug: string,
	type: 'blog'
): Promise<{ content: string; metaData: PostMetaData } | null>;
export async function getArticleForSlug(slug: string, type: 'blog' | 'docs') {
	let articles: Record<string, () => Promise<string>>;
	if (type === 'blog') {
		articles = import.meta.glob('/blogposts/**/*.{md,meta.md}', {
			as: 'raw'
		}) as Record<string, () => Promise<string>>;
	} else {
		articles = import.meta.glob('/docs/**/*.{md,meta.md}', {
			as: 'raw'
		}) as Record<string, () => Promise<string>>;
	}

	for (const [path, contentLoader] of Object.entries(articles)) {
		if (!path.endsWith('.meta.md')) {
			continue;
		}
		const parsed = frontmatter(await contentLoader());
		const metaData = parsed.attributes as PostMetaData | DocsMetaData;
		if (metaData.slug.trim() === slug) {
			const mdPath = path.replace('.meta.md', '.md');
			return { content: await articles[mdPath](), metaData };
		}
	}
	return null;
}

export async function getBlogPostForSlug(slug: string) {
	return getArticleForSlug(slug, 'blog');
}

export async function getDocsArticleForSlug(slug: string) {
	return getArticleForSlug(slug, 'docs');
}

export function getSeoImageUrl(titleData?: { title: string; subTitle: string }) {
	let urlPath = `other/socialcard`;
	if (titleData) {
		urlPath += `?title=${encodeURIComponent(titleData.title)}&subTitle=${encodeURIComponent(titleData.subTitle)}`;
	}
	return withBaseUrl(ClientSettings.SiteBaseUrl, urlPath);
}
